import axios, { AxiosResponse } from 'axios';
import Big from 'big.js';
import { instanceToInstance, plainToInstance } from 'class-transformer';
import { ClassConstructor } from 'class-transformer/types/interfaces';
import { DataResultsDto } from './data-results.dto';

/**
 * Talks to the resource endpoints under /api/resource by URL.
 *
 * Its predecessor took a resource name and built the URL from it, because Spring Data REST derived the URL from the
 * repository the same way and the two conventions had to agree. They no longer do anything: the endpoints are hand
 * written controllers, so the URL is just a URL and the caller writes it out. The same client also walked the pages of
 * every collection - 500 rows a request, until the last page came back - to reassemble what the caller wanted all
 * along. The endpoints answer with the whole array now, so there is nothing to reassemble.
 */
export interface ResourceRequestOptions {
  modelClass?: ClassConstructor<any>;
  contentType?: 'application/json' | 'multipart/form-data' | 'application/json-patch+json';
}

const toModel = <T>(data: any, modelClass?: ClassConstructor<any>): T => (modelClass != null ? plainToInstance(modelClass, data) : data);

const failure = <T>(error: any): DataResultsDto<T> =>
  new DataResultsDto<T>(
    true,
    error.response?.status,
    error.response?.data?.error ?? error.response?.data,
    error.response?.data?.message ?? error.response?.data,
    error.response?.data,
  );

/** class-transformer leaves Big.js amounts as objects; the API reads them as strings. */
const mapBigToString = (entity: any) => {
  Object.keys(entity).forEach((key) => {
    if (entity[key] instanceof Big) {
      entity[key] = entity[key].toFixed();
    }
  });
};

const toFormData = (obj: any) => {
  const formData = new FormData();
  Object.keys(obj).forEach((key) => formData.append(key, obj[key]));
  return formData;
};

export class ResourceClientService {
  public static findAll<ModelType>(url: string, filters?: any, options?: ResourceRequestOptions): Promise<DataResultsDto<ModelType[]>> {
    return axios
      .get(url, { params: filters })
      .then((response: AxiosResponse) => new DataResultsDto<ModelType[]>(false, undefined, undefined, undefined, toModel<ModelType[]>(response.data ?? [], options?.modelClass)))
      .catch((error) => failure<ModelType[]>(error));
  }

  public static findOne<ModelType>(url: string, filters?: any, options?: ResourceRequestOptions): Promise<DataResultsDto<ModelType>> {
    return axios
      .get(url, { params: filters })
      .then((response: AxiosResponse) => new DataResultsDto<ModelType>(false, undefined, undefined, undefined, toModel<ModelType>(response.data, options?.modelClass)))
      .catch((error) => failure<ModelType>(error));
  }

  public static create<ModelType>(url: string, payload: Partial<ModelType>, options?: ResourceRequestOptions): Promise<DataResultsDto<ModelType>> {
    const contentType = options?.contentType || 'application/json';
    return axios({
      url,
      method: 'POST',
      data: contentType === 'multipart/form-data' ? toFormData(payload) : payload,
      headers: { Accept: 'application/json', 'Content-Type': contentType },
    })
      .then((response: AxiosResponse) => new DataResultsDto<ModelType>(false, undefined, undefined, undefined, toModel<ModelType>(response.data, options?.modelClass)))
      .catch((error) => failure<ModelType>(error));
  }

  public static update<ModelType>(url: string, payload: Partial<ModelType>, options?: ResourceRequestOptions): Promise<DataResultsDto<ModelType>> {
    const stringified = instanceToInstance(payload);
    mapBigToString(stringified);
    return axios
      .patch(url, stringified, { headers: { 'Content-Type': options?.contentType || 'application/json' } })
      .then((response: AxiosResponse) => new DataResultsDto<ModelType>(false, undefined, undefined, undefined, toModel<ModelType>(response.data, options?.modelClass)))
      .catch((error) => failure<ModelType>(error));
  }

  public static delete<ModelType>(url: string, options?: ResourceRequestOptions): Promise<DataResultsDto<ModelType>> {
    return axios
      .delete(url)
      .then((response: AxiosResponse) => new DataResultsDto<ModelType>(false, undefined, undefined, undefined, toModel<ModelType>(response.data, options?.modelClass)))
      .catch((error) => failure<ModelType>(error));
  }
}

/**
 * The same requests for react-query, which wants a rejected promise rather than a result object it has to unpack.
 */
export class ResourceQueryService {
  public static findAll<ModelType>(url: string, filters?: any, options?: ResourceRequestOptions): Promise<ModelType[]> {
    return axios.get(url, { params: filters }).then((response: AxiosResponse) => toModel<ModelType[]>(response.data ?? [], options?.modelClass));
  }

  public static findOne<ModelType>(url: string, filters?: any, options?: ResourceRequestOptions): Promise<ModelType> {
    return axios.get(url, { params: filters }).then((response: AxiosResponse) => toModel<ModelType>(response.data, options?.modelClass));
  }

  /** The views are keyed by their aggregate id, which is a column rather than the row id the URL takes. */
  public static findOneByField<ModelType>(url: string, fieldName: string, value?: string, options?: ResourceRequestOptions): Promise<ModelType> {
    if (value === undefined) throw new Error('undefined find by id');
    return this.findAll<ModelType>(url, { [fieldName]: value }, options).then((rows) => {
      if (rows.length > 0) return rows[0];
      throw new Error('undefined find by id');
    });
  }
}
